import type {
    FloatingContext,
    Placement,
    UseFloatingOptions,
    UseInteractionsReturn,
    UseListNavigationProps,
    ReferenceType,
    UseHoverProps,
    UseDismissProps, UseClickProps,
} from '@floating-ui/react';

import {
    useFloatingParentNodeId,
    flip,
    offset,
    safePolygon,
    shift,
    size,
    useClick,
    useDismiss,
    useFloating as useFloatingHook,
    useFloatingNodeId,
    useTypeahead,
    useHover,
    useInteractions, useListNavigation, useRole, useFocus,
} from '@floating-ui/react';
import {useControlledState} from '@pb/kit-hooks-use-controlled-state';

// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import React, {useRef} from 'react';
import type {UseArrowProps, UseArrowReturnType} from './use-arrow';
import {useComponentDidUpdate} from '@pb/kit-hooks-use-component-did-update';

import {useArrow} from './use-arrow';


import type {ComponentProps} from 'react';
import type {SetShown} from '../typedefs';
import type {UseUpdateOptions} from './use-update';
import {useUpdate} from './use-update';

const Strategies = {
    ABSOLUTE: 'absolute',
    FIXED: 'fixed',
} as const;

/**
 * $FlowIgnoreMe WHY: Floating UI has no Flow type definitions
 **/
type _UseFloatingOptions = UseFloatingOptions;

export interface UseFloatingProps extends _UseFloatingOptions {
    /**
     * Positioning strategy
     * @deprecated It will be removed in 6 months 03-12-2026. Use strategy instead.
     * */
    type?: 'ABSOLUTE' | 'FIXED',

    /**
     * External 'shown' state, use with 'setShown' to control the Dropdown visibility.
     * */
    shown?: boolean,

    /**
     * Setter for the external 'shown' state
     * */
    setShown?: SetShown,

    /**
     * Max possible height of the Dropdown, in px
     */
    maxHeight?: number,

    /**
     * Max possible width of the Dropdown, in px
     */
    maxWidth?: number,

    /**
     * Correction of the floating element vertical position, in pixels
     */
    verticalOffset?: number,
    /**
     * Correction of the floating element horizontal position, in pixels
     */
    horizontalOffset?: number,

    /**
     * ARIA role
     * */
    role?: 'tooltip' | 'dialog' | 'alertdialog' | 'menu' | 'listbox' | 'grid' | 'tree' | 'select' | 'label' | 'combobox',

    /**
     * If Floating list navigation is enabled. Use for Dropdowns with a list of options
     * */
    listNavigationEnabled?: boolean,

    /**
     * $FlowIgnoreMe WHY: Floating UI has no Flow type definitions. Allowed placements for the floating element
     **/
    allowedPlacements?: Placement[],

    /**
     * $FlowIgnoreMe WHY: Floating UI has no Flow type definitions. Additional middlewares for the useFloating hook
     **/
    middlewares?: UseFloatingOptions['middleware'],

    /**
     * If Floating UI Arrow should be enabled
     * */
    arrowEnabled?: boolean,

    focusEnabled?: boolean,
    clickEnabled?: boolean,
    hoverEnabled?: boolean,
    typeaheadEnabled?: boolean,
    /**
     * Callback on toggle shown state, use it in case you don't want to use the external 'shown' state
     * */
    onToggle?: (shown: boolean) => unknown,

    /*
    * Width of the Floating element should be equal to the width of the anchor element
    * */
    equalWidth?: boolean,

    /**
     * $FlowIgnoreMe WHY: Floating UI has no Flow type definitions
     **/
    listNavigationProps?: Partial<UseListNavigationProps>,
    /**
     * $FlowIgnoreMe WHY: Floating UI has no Flow type definitions
     **/
    hoverProps?: Partial<UseHoverProps>,
    arrowProps?: UseArrowProps,

    /**
     * $FlowIgnoreMe WHY: Floating UI has no Flow type definitions
     */
    dismissProps?: UseDismissProps,

    /**
     * $FlowIgnoreMe WHY: Floating UI has no Flow type definitions
     */
    clickProps?: UseClickProps,

    /**
     * Options for the update util
     * */
    updateProps?: UseUpdateOptions,
}

type NativeProps = ComponentProps<'div'>;

type ElementsOrNull = Array<HTMLElement | null>;
type LabelsOrNull = Array<string | null>;
export interface getFloatingElPropsReturnType extends NativeProps {
    /**
     * $FlowIgnoreMe WHY: Floating UI has no Flow type definitions
     **/
    getContext: () => FloatingContext,

    listItemsRef: React.MutableRefObject<ElementsOrNull>,
    labelsRef: React.MutableRefObject<LabelsOrNull>,
    nodeId: string,
}

export type UseFloatingReturnType = {
    anchorRef: (node: Element | null) => void,
    /**
     * $FlowIgnoreMe WHY: Floating UI has no Flow type definitions
     **/
    getAnchorProps: UseInteractionsReturn['getReferenceProps'],
    /**
     * $FlowIgnoreMe WHY: Floating UI has no Flow type definitions
     **/
    floatingElRef: ReturnType<typeof useFloatingHook>['refs']['setFloating'],
    /**
     * $FlowIgnoreMe WHY: Floating UI has no Flow type definitions. Use this if Dropdown should be positioned not in center of the anchor element, but at the endIcon
     **/
    positionRef: (node: ReferenceType | null) => void,
    getFloatingElProps: (props?: ComponentProps<'div'>) => getFloatingElPropsReturnType,
    /**
     * $FlowIgnoreMe WHY: Floating UI has no Flow type definitions
     **/
    getItemProps: UseInteractionsReturn['getItemProps'],
    /**
     * $FlowIgnoreMe WHY: Floating UI has no Flow type definitions
     **/
    getContext: () => FloatingContext,
    arrow: UseArrowReturnType['Component'],
    activeIndex: number | null,
    setActiveIndex: (index: number | null) => void,
};

/**
 * Basic hook for creating floating elements with Floating UI. Use it with FloatingPopover component.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/utils-floating--documentation}
 * */
export const useFloating = ({
    maxHeight,
    maxWidth,
    verticalOffset = 0,
    horizontalOffset = 0,
    role,
    placement,
    allowedPlacements,
    type,
    strategy = type ? Strategies[type] : undefined,
    shown,
    setShown,
    hoverEnabled = false,
    clickEnabled = false,
    focusEnabled = false,
    typeaheadEnabled = false,
    middlewares = [],
    listNavigationEnabled = false,
    arrowEnabled = false,
    onToggle,
    equalWidth = false,
    listNavigationProps,
    hoverProps,
    dismissProps,
    clickProps,
    arrowProps,
    updateProps,
    ...props
}: UseFloatingProps): UseFloatingReturnType => {
    const [open, setOpen] = useControlledState(shown, setShown, false);

    const elementsRef = useRef<Array<HTMLElement | null>>([]);
    const labelsRef = useRef<Array<string | null>>([]);

    useComponentDidUpdate(() => {
        onToggle?.(open);
    }, [open]);

    const [activeIndex, setActiveIndex] = useControlledState(
        listNavigationProps?.activeIndex,
        listNavigationProps?.onNavigate,
        null,
    );

    const nodeId = useFloatingNodeId();

    const nested = useFloatingParentNodeId() !== null;

    const arrow = useArrow({
        enabled: arrowEnabled,
        ...arrowProps,
    });

    const {whileElementsMounted, contextRef} = useUpdate(setOpen, updateProps);

    const {refs, floatingStyles, context} = useFloatingHook({
        ...props,
        placement: placement || allowedPlacements?.[0],
        nodeId,
        strategy,
        open,
        onOpenChange: setOpen,
        whileElementsMounted,
        middleware: [
            offset({
                mainAxis: verticalOffset + arrow.height,
                crossAxis: horizontalOffset,
            }),
            flip({
                fallbackPlacements: allowedPlacements,
            }),
            shift(),
            size({
                apply: ({availableWidth, availableHeight, elements}) => {

                    Object.assign(elements.floating.style, {
                        maxWidth: `${
                            !equalWidth && maxWidth && maxWidth <= availableWidth
                                ? maxWidth
                                : availableWidth
                        }px`,
                        maxHeight: `${
                            maxHeight && maxHeight <= availableHeight
                                ? maxHeight
                                : availableHeight
                        }px`,
                    });

                    if (equalWidth) {
                        const {width} = elements.reference.getBoundingClientRect();
                        Object.assign(elements.floating.style, {
                            width: `${width}px`,
                        });
                    }
                },
            }),
            arrow.middleware,
            ...middlewares,
        ],
    });

    contextRef.current = context;

    const dismiss = useDismiss(context, dismissProps);

    const hover = useHover(context, {
        enabled: hoverEnabled,
        handleClose: safePolygon(),
        delay: {
            open: 150, // to prevent opening when moving mouse over a list of anchors (eg. in a menu)
        },
        ...hoverProps,
    });

    const click = useClick(context, {
        enabled: clickEnabled,
        ...clickProps,
    });

    const focus = useFocus(context, {
        enabled: focusEnabled,
    });

    const listNavigation = useListNavigation(context, {
        enabled: listNavigationEnabled,
        listRef: elementsRef,
        activeIndex,
        loop: true,
        onNavigate: setActiveIndex,
        nested,
        ...listNavigationProps,
    });

    const typeahead = useTypeahead(context, {
        enabled: typeaheadEnabled,
        listRef: labelsRef,
        activeIndex,
        onMatch: setActiveIndex,
    });

    const roleInteraction = useRole(context, {role});

    const {getReferenceProps, getFloatingProps, getItemProps} = useInteractions([
        dismiss,
        roleInteraction,
        listNavigation,
        typeahead,
        hover,
        click,
        focus,
    ]);

    return {
        anchorRef: refs.setReference,
        getAnchorProps: _props => ({
            ..._props,
            ...getReferenceProps(_props), // props is merged twice cause getReferenceProps returns only own props, not all props from props
        }),
        floatingElRef: refs.setFloating,
        positionRef: refs.setPositionReference,
        getItemProps: itemProps => ({
            ...itemProps,
            ...getItemProps(itemProps), // itemProps is merged twice cause getItemProps returns only own props, not all props from itemProps
        }),
        arrow: arrow.Component,
        activeIndex,
        setActiveIndex,
        getContext: () => context,
        getFloatingElProps: _props => ({
            ..._props,
            ...getFloatingProps(_props), // props is merged twice cause getFloatingProps returns only own props, not all props from props
            // for some magic reason context doesn't work if is propagated as an object
            getContext: () => context,
            style: {
                ...floatingStyles,
                ..._props?.style,
            },
            listItemsRef: elementsRef,
            labelsRef,
            nodeId,
        }),
    };
};
